import { Checkbox, Button, Position, Spinner, Pagination, Tab } from '@consigli/facade';
import { useProjectId, useSelectedCard } from '@consigli/hooks';
import {
  Finding,
  FindingCommenter,
  FindingStatus,
  useConvertFindingToDocument,
} from '@consigli/types';
import clsx from 'clsx';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { HiOutlineAdjustments } from 'react-icons/hi';

import { useViewer } from '@/features/document-viewer/use-viewer';
import { ViewerMode } from '@/features/document-viewer/use-viewer-context';
import { isValidDocumentType } from '@/util/document-check';
import { CheckableFinding } from '@/util/types';

import { BatchUpdateButton } from './components/batch-update-button';
import { FindingsCard } from './components/findings-card';
import { mapActionNameToStates } from './findings-content';
import { useFindingsContext } from './findings-context';
import { FindingsFilter } from './findings-filter';
import { FindingBlob } from './findings-wrapper';
import { useResetPage } from './use-reset-page';

interface FindingsListProps {
  findings: Finding[];
  findingsCount: number;
  uniqueBlobs: FindingBlob[];
  commenters: FindingCommenter[];
  uniqueLabels: string[];
  statusTabs: FindingStatus[];
  statusCount: Record<string, number>;
  itemsOnPage: number;
  isFetchingFindings: boolean;
  isFetchingStatusCount: boolean;
}

export const processing = FindingStatus.PROCESSING;

export const FindingsList = ({
  findings,
  findingsCount,
  uniqueBlobs,
  commenters,
  uniqueLabels,
  statusTabs,
  statusCount,
  itemsOnPage,
  isFetchingFindings,
  isFetchingStatusCount,
}: FindingsListProps) => {
  const { t } = useTranslation();
  const {
    page,
    setPage,
    pageSize,
    setSearchTerm,
    selectedStatusTab,
    setSelectedStatusTab,
    setSelectedBlobIds,
    scrollPosition,
    setScrollPosition,
    containerRef,
    pageRecords,
    setPageRecords,
    setCurrentFinding,
    currentFinding,
  } = useFindingsContext();
  const { reset } = useResetPage();
  const convertFindingToDocument = useConvertFindingToDocument(t);
  const { setSelectedCard } = useSelectedCard('');
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectCheckedCount, setSelectCheckedCount] = useState(0);
  const [showButtons, setShowButtons] = useState(false);
  const { openDocumentViewer } = useViewer();
  const projectId = useProjectId();
  const [displayFilter, setDisplayFilter] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      if (scrollTop !== undefined) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          setScrollPosition(scrollTop);
        }, 200);
      }
    }
  }, [containerRef, setScrollPosition]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollPosition;
    }
  });

  useEffect(() => {
    if (!displayFilter) {
      setSearchTerm('');
      setSelectedBlobIds([]);
      setPage(1);
      setScrollPosition(0);
    }
  }, [displayFilter, setPage, setSearchTerm, setSelectedBlobIds, setScrollPosition]);

  const filteredButtonKeys = useMemo(() => {
    // Match tab order (exclude PROCESSING; handled by forward button)
    const preferredButtonKeysOrder = [
      FindingStatus.ACTIVE,
      FindingStatus.PROCESSING,
      FindingStatus.PROCESSED,
      FindingStatus.NOT_RELEVANT,
      FindingStatus.DELETED,
    ];
    const keys = Object.keys(mapActionNameToStates).filter(
      (item: string) => item !== selectedStatusTab,
    );
    return keys.sort((a, b) => {
      const indexA = preferredButtonKeysOrder.indexOf(a as FindingStatus);
      const indexB = preferredButtonKeysOrder.indexOf(b as FindingStatus);
      return indexA - indexB;
    });
  }, [selectedStatusTab]);
  const resetSelectAll = useCallback(() => {
    setSelectAllChecked(false);
    setPageRecords((prevData: CheckableFinding[]) =>
      prevData.map(
        (item: CheckableFinding) =>
          item && { ...item, checked: item.checked ? false : item.checked },
      ),
    );
  }, [setPageRecords]);

  const handleTabClick = useCallback(
    (tab: string) => {
      setSelectedStatusTab(tab as FindingStatus);
      setPage(1);
      resetSelectAll();
      setScrollPosition(0);
    },
    [setSelectedStatusTab, setPage, resetSelectAll, setScrollPosition],
  );
  const handlePageChange = useCallback(
    (page: number) => {
      reset(page);
    },
    [reset],
  );
  const handleCheckboxClick = (record: CheckableFinding) => {
    setPageRecords((prevData: CheckableFinding[]) =>
      prevData.map((item: CheckableFinding) =>
        item.id === record.id ? { ...item, checked: !item.checked } : item,
      ),
    );
  };
  const handleSelectAllCheckboxClick = () => {
    setSelectAllChecked(!selectAllChecked);
    setPageRecords((prevData: CheckableFinding[]) =>
      prevData.map((item: CheckableFinding) => item && { ...item, checked: !selectAllChecked }),
    );
  };
  const onComplete = () => {
    setShowButtons(false);
    setSelectAllChecked(false);
    setPageRecords((prevData: CheckableFinding[]) =>
      prevData.map((item: CheckableFinding) => ({ ...item, checked: false })),
    );
  };
  const onCommentsClick = useCallback(
    (record: CheckableFinding) => {
      return (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setSelectedCard(record.id.toString());
        openDocumentViewer(convertFindingToDocument(record), ViewerMode.CommentPreview);
      };
    },
    [setSelectedCard, openDocumentViewer, convertFindingToDocument],
  );
  const getOnClickHandler = useCallback(
    (record: CheckableFinding) => {
      return () => {
        setCurrentFinding(record);
        setSelectedCard(record.id.toString());
        if (record.fileName != null && isValidDocumentType(record.fileName)) {
          openDocumentViewer(convertFindingToDocument(record), ViewerMode.DocumentFullscreen);
        }
      };
    },
    [setCurrentFinding, setSelectedCard, openDocumentViewer, convertFindingToDocument],
  );

  useEffect(() => {
    setPageRecords(findings as CheckableFinding[]);
  }, [findings, setPageRecords]);

  useEffect(() => {
    // showing buttons to change multiple status through popup
    const newCount = pageRecords?.reduce(
      (count: number, item: CheckableFinding) => count + (item.checked ? 1 : 0),
      0,
    );
    setSelectCheckedCount(newCount);
    setShowButtons(newCount > 1);

    // Update select all checkbox based on individual selections
    if (newCount === 0) {
      setSelectAllChecked(false);
    } else if (newCount === pageRecords.length) {
      setSelectAllChecked(true);
    } else if (selectAllChecked && newCount < pageRecords.length) {
      // If "Select All" was previously checked but now some items are unchecked
      setSelectAllChecked(false);
    }
  }, [pageRecords, selectAllChecked]);

  return (
    <>
      <div className="relative">
        <div className="flex flex-col h-[calc(100vh-17rem)] 2xl:h-[calc(100vh-23rem)]">
          <div className="flex flex-col sm:flex-row justify-between items-center flex-wrap mt-0 2xl:mt-4 mb-2">
            {statusTabs.length === 0 && isFetchingStatusCount ? (
              <Spinner />
            ) : (
              <div className="flex flex-row flex-wrap">
                {statusTabs.map((tab: FindingStatus) => (
                  <Tab
                    key={tab}
                    label={t(`findings.status.${tab}`)}
                    selected={selectedStatusTab === tab}
                    onClick={() => handleTabClick(tab)}
                    count={statusCount[tab]}
                    icon={mapActionNameToStates[tab].icon}
                  />
                ))}
              </div>
            )}
            <div className="pt-4 xl:pt-0 font-medium flex">
              <Button
                className="flex items-center cursor-pointer text-sm mr-2"
                aria-hidden
                onClick={() => setDisplayFilter((prevData) => !prevData)}
                icon={displayFilter ? FaTimes : HiOutlineAdjustments}
                iconPosition={Position.RIGHT}
                tertiary
                rounded
              >
                {displayFilter ? t('findings.clear-filter') : t('findings.add-filter')}
              </Button>
            </div>
          </div>
          <span
            className={clsx(
              'transition-opacity duration-500 mb-2',
              displayFilter ? 'opacity-100' : 'opacity-0',
            )}
          >
            {displayFilter && (
              <FindingsFilter
                selectAllChecked={selectAllChecked}
                handleSelectAllCheckboxClick={handleSelectAllCheckboxClick}
                uniqueBlobs={uniqueBlobs}
                commenters={commenters}
                uniqueLabels={uniqueLabels}
              />
            )}
          </span>
          {isFetchingFindings ? (
            <div className="flex h-full w-full items-center justify-center">
              <Spinner />
            </div>
          ) : pageRecords.length === 0 ? (
            <div className="bg-day-light-3 w-full h-full flex flex-col items-center justify-center">
              <div className="text-day-neutral-subtle text-xl font-medium py-4">
                {t('findings.no-findings-by-filter')}
              </div>
            </div>
          ) : (
            <div
              className="mb-4 overflow-auto"
              ref={containerRef as RefObject<HTMLInputElement>}
              onScroll={handleScroll}
            >
              {pageRecords?.map((record: CheckableFinding) => (
                <div key={record.id} className="flex items-center justify-between">
                  <div className="pl-1 pr-4">
                    <Checkbox
                      value={record.id.toString()}
                      label=""
                      id={record.id.toString()}
                      checked={record.checked}
                      onChange={() => handleCheckboxClick(record)}
                    />
                  </div>
                  <div className="flex-1">
                    <FindingsCard
                      finding={record}
                      itemsOnPage={itemsOnPage}
                      onCheckboxClick={() => handleCheckboxClick(record)}
                      onCardClick={getOnClickHandler(record)}
                      selectedCard={currentFinding?.id === record.id}
                      onCommentsClick={onCommentsClick(record)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="pt-2 2xl:pt-4 flex flex-col md:flex-row justify-center items-center gap-8 text-day-neutral-dark">
          <div className="text-sm font-semibold">
            {t('findings.showing_findings', { count: findingsCount })}
          </div>
          <Pagination
            currentPage={page}
            totalCount={findingsCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </div>
        <BatchUpdateButton
          buttonKeys={filteredButtonKeys}
          projectId={projectId}
          count={selectCheckedCount}
          showButtons={showButtons}
          setShowButtons={setShowButtons}
          itemsOnPage={itemsOnPage}
          onComplete={onComplete}
        />
      </div>
    </>
  );
};
